import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { CheckCircle2, ChevronDown, ClipboardList, Loader2, Send } from "lucide-react";
import { portalFetch, KyraApiError } from "@/lib/api";
import { useAnamnesis, useMe } from "@/lib/queries";
import type { StudentAnamnesis, StudentAnamnesisData } from "@/lib/types";
import {
  ANAMNESE_LABELS,
  ANAMNESE_SECTIONS,
  computeAnamneseDiff,
  type AnamneseFieldDef,
  type AnamneseFieldKey,
} from "@/lib/anamnese-fields";
import { Spinner } from "@/components/Spinner";
import { cn } from "@/lib/cn";

type AnyVal = StudentAnamnesisData[AnamneseFieldKey];

/**
 * Anamnese self-service — o aluno preenche a própria ficha e envia pro
 * personal. O trainer manda o link rotina.bio/s/:slug/anamnese (a guarda do
 * AppShell cuida do login + redirect de volta). Campos/labels espelham o
 * formulário do trainer no desktop; envio manda só o diff (UPSERT parcial)
 * e dispara notificação pro personal no Worker.
 */
export function AnamneseView() {
  const { slug = "" } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const me = useMe();
  const anamnesis = useAnamnesis();

  const [draft, setDraft] = useState<Partial<StudentAnamnesisData>>({});
  // Snapshot da base congelado na hidratação: o diff é sempre contra o que o
  // ALUNO viu ao abrir o form, não contra o cache vivo — um refetch em
  // background (staleTime 30s / focus) não pode inflar o patch nem fazer o
  // envio reverter edição concorrente do trainer em campos não tocados aqui.
  const [baseSnapshot, setBaseSnapshot] = useState<StudentAnamnesisData>({});
  const [hydrated, setHydrated] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const sentHeadingRef = useRef<HTMLHeadingElement>(null);

  // Hidrata o draft UMA vez quando a ficha chega (refetches não podem
  // sobrescrever o que o aluno está digitando).
  useEffect(() => {
    if (hydrated || !anamnesis.data?.data) return;
    setDraft({ ...anamnesis.data.data });
    setBaseSnapshot({ ...anamnesis.data.data });
    setHydrated(true);
  }, [hydrated, anamnesis.data]);

  // Move o foco pro título da confirmação — sem isso, leitor de tela fica
  // "preso" no botão de enviar que sumiu e o envio passa despercebido.
  useEffect(() => {
    if (sent) sentHeadingRef.current?.focus();
  }, [sent]);

  const patch = useMemo(() => computeAnamneseDiff(draft, baseSnapshot), [draft, baseSnapshot]);
  const dirtyCount = Object.keys(patch).length;

  const updateField = (key: AnamneseFieldKey, value: AnyVal | null) => {
    setDraft((prev) => ({ ...prev, [key]: value }));
  };

  const handleSubmit = async () => {
    if (submitting) return;
    setSubmitting(true);
    setError(null);
    try {
      const updated = await portalFetch<StudentAnamnesis>("/student-portal/me/anamnesis", {
        method: "POST",
        body: { data: patch },
      });
      if (updated.data) {
        queryClient.setQueryData(["anamnesis"], updated);
        setDraft({ ...updated.data });
        setBaseSnapshot({ ...updated.data });
      } else {
        // Save ok mas a releitura falhou no Worker (data: null) — NÃO gravar
        // no cache (baseSnapshot degradaria pra {} e o diff explodiria);
        // refetch em background reconcilia sem tocar no draft.
        void queryClient.invalidateQueries({ queryKey: ["anamnesis"] });
      }
      setSent(true);
      window.scrollTo({ top: 0 });
    } catch (err) {
      if (err instanceof KyraApiError && err.status === 401) {
        const from = encodeURIComponent(`/s/${slug}/anamnese`);
        navigate(`/s/${slug}/login?redirectedFrom=${from}`, { replace: true });
        return;
      }
      setError("Não consegui enviar. Verifique sua conexão e tente de novo.");
    } finally {
      setSubmitting(false);
    }
  };

  if (anamnesis.isLoading) return <Spinner fullscreen />;

  // Tela de erro fullscreen SÓ quando não há nada pra mostrar. Um refetch em
  // background que falha (isError com data em cache) não pode trocar o form
  // pelo erro — o aluno perderia o que digitou; mostramos um banner inline.
  if (anamnesis.isError && !anamnesis.data) {
    return (
      <div className="animate-fade-in px-5 pt-8">
        <h1 className="mb-3 font-display text-xl text-ink">Anamnese</h1>
        <div
          role="alert"
          className="rounded-xl border border-danger/40 bg-danger/10 px-3 py-2 text-sm text-danger"
        >
          Não consegui carregar sua ficha. Puxe pra atualizar ou tente mais tarde.
        </div>
      </div>
    );
  }

  const trainerName = me.data?.trainer.full_name ?? "seu personal";

  if (sent) {
    return (
      <div className="animate-fade-in px-5 pt-16 text-center">
        <CheckCircle2 size={48} className="mx-auto mb-4 text-accent-soft" aria-hidden />
        <h1
          ref={sentHeadingRef}
          tabIndex={-1}
          className="mb-2 font-display text-2xl text-ink outline-none"
        >
          Anamnese enviada!
        </h1>
        <p className="mb-8 text-sm text-muted">
          {trainerName} recebeu suas respostas e vai usar tudo isso pra montar um plano do seu
          jeito.
        </p>
        <div className="flex flex-col items-center gap-3">
          <Link
            to={`/s/${slug}`}
            className="inline-flex items-center gap-2 rounded-xl bg-accent px-5 py-3 text-sm font-medium text-ink shadow-sm transition-colors hover:bg-accent-hover"
          >
            Voltar ao início
          </Link>
          <button
            onClick={() => setSent(false)}
            className="text-sm text-muted transition-colors hover:text-ink"
          >
            Revisar minhas respostas
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="animate-fade-in px-5 pt-8">
      <header className="mb-6">
        <div className="mb-2 flex items-center gap-2 text-accent-soft">
          <ClipboardList size={18} aria-hidden />
          <span className="text-[11px] font-semibold uppercase tracking-wider">Sua ficha</span>
        </div>
        <h1 className="mb-1 font-display text-2xl text-ink">Anamnese</h1>
        <p className="text-sm text-muted">
          Preencha o que souber — suas respostas vão direto pra {trainerName} montar seu plano.
          Nenhum campo é obrigatório.
        </p>
      </header>

      {anamnesis.isError && (
        <div
          role="alert"
          className="mb-4 rounded-xl border border-border-subtle bg-surface px-3 py-2 text-sm text-muted"
        >
          Não consegui atualizar sua ficha agora — suas respostas continuam aqui.
        </div>
      )}

      <div className="flex flex-col gap-3">
        {ANAMNESE_SECTIONS.map((section, idx) => (
          <SectionCard
            key={section.id}
            title={section.title}
            icon={section.icon}
            fields={section.fields}
            draft={draft}
            updateField={updateField}
            defaultOpen={idx === 0}
          />
        ))}
      </div>

      {error && (
        <div
          role="alert"
          className="mt-4 rounded-xl border border-danger/40 bg-danger/10 px-3 py-2 text-sm text-danger"
        >
          {error}
        </div>
      )}

      <div className="mt-6">
        <button
          onClick={() => void handleSubmit()}
          disabled={submitting}
          className="inline-flex w-full items-center justify-center gap-2 rounded-xl bg-accent px-4 py-3.5 font-medium text-ink shadow-sm transition-colors hover:bg-accent-hover disabled:cursor-not-allowed disabled:opacity-60"
        >
          {submitting ? (
            <>
              <Loader2 size={16} className="animate-spin" aria-hidden /> Enviando…
            </>
          ) : (
            <>
              <Send size={16} aria-hidden /> Enviar para o personal
            </>
          )}
        </button>
        <p className="mt-2 text-center text-[11px] text-faint" aria-live="polite">
          {dirtyCount > 0
            ? `${dirtyCount} ${dirtyCount === 1 ? "campo alterado" : "campos alterados"}`
            : "Você pode voltar aqui e atualizar quando quiser."}
        </p>
      </div>
    </div>
  );
}

// ============================================================
// SectionCard — <details> colapsável no estilo do portal.
// ============================================================

interface SectionCardProps {
  title: string;
  icon: React.ComponentType<{ size?: number | string; className?: string }>;
  fields: AnamneseFieldDef[];
  draft: Partial<StudentAnamnesisData>;
  updateField: (key: AnamneseFieldKey, value: AnyVal | null) => void;
  defaultOpen?: boolean;
}

function SectionCard({ title, icon: Icon, fields, draft, updateField, defaultOpen }: SectionCardProps) {
  const filledCount = fields.filter((f) => {
    const v = draft[f.key];
    return v !== null && v !== undefined && !(typeof v === "string" && v.trim() === "");
  }).length;

  return (
    <details
      open={defaultOpen}
      className="group overflow-hidden rounded-2xl border border-border-subtle bg-surface"
    >
      <summary className="flex cursor-pointer list-none items-center justify-between gap-3 px-5 py-4 transition-colors hover:bg-surface-hover">
        <div className="flex min-w-0 items-center gap-2.5">
          <Icon size={16} className="shrink-0 text-accent-soft" aria-hidden />
          <span className="truncate font-medium text-ink">{title}</span>
          <span className="shrink-0 text-[11px] text-faint">
            {filledCount}/{fields.length}
          </span>
        </div>
        <ChevronDown
          size={16}
          className="shrink-0 text-faint transition-transform group-open:rotate-180"
          aria-hidden
        />
      </summary>
      <div className="flex flex-col gap-4 border-t border-border-subtle px-5 py-4">
        {fields.map((field) => (
          <FieldControl
            key={field.key}
            field={field}
            value={draft[field.key] as AnyVal | undefined}
            onChange={(v) => updateField(field.key, v)}
          />
        ))}
      </div>
    </details>
  );
}

// ============================================================
// FieldControl — input apropriado pelo type, no estilo do portal.
// ============================================================

const inputCls =
  "w-full rounded-xl border border-border-subtle bg-canvas/70 px-4 py-3 text-ink outline-none transition-colors placeholder:text-faint focus:border-accent-soft";

/**
 * Wrapper de <select> com chevron próprio — appearance-none esconde a seta
 * nativa e sem ela o select parece input de texto.
 */
function SelectShell({ muted, children }: { muted: boolean; children: React.ReactNode }) {
  return (
    <div className="relative">
      {children}
      <ChevronDown
        size={16}
        className={cn(
          "pointer-events-none absolute right-4 top-1/2 -translate-y-1/2",
          muted ? "text-faint" : "text-muted",
        )}
        aria-hidden
      />
    </div>
  );
}

const SENTINEL_NONE = "__none__";

interface FieldControlProps {
  field: AnamneseFieldDef;
  value: AnyVal | undefined;
  onChange: (v: AnyVal | null) => void;
}

function FieldControl({ field, value, onChange }: FieldControlProps) {
  const label = ANAMNESE_LABELS[field.key];
  const stringValue = value === null || value === undefined ? "" : String(value);

  return (
    <label className="flex flex-col gap-1.5">
      <span className="text-xs font-medium text-muted">{label}</span>
      {renderInput(field, stringValue, value, onChange)}
    </label>
  );
}

function renderInput(
  field: AnamneseFieldDef,
  stringValue: string,
  value: AnyVal | undefined,
  onChange: (v: AnyVal | null) => void,
) {
  switch (field.type) {
    case "textarea":
      return (
        <textarea
          rows={field.rows ?? 3}
          value={stringValue}
          placeholder={field.placeholder}
          maxLength={2000}
          onChange={(e) => onChange(e.target.value === "" ? null : e.target.value)}
          className={cn(inputCls, "resize-none text-sm leading-relaxed")}
        />
      );

    case "number":
      return (
        <input
          type="number"
          inputMode="decimal"
          step={field.step ?? "any"}
          value={stringValue}
          placeholder={field.placeholder}
          onChange={(e) => {
            const raw = e.target.value;
            if (raw === "") {
              onChange(null);
              return;
            }
            const n = Number(raw);
            onChange(Number.isFinite(n) ? n : null);
          }}
          className={inputCls}
        />
      );

    case "date":
      return (
        <input
          type="date"
          value={stringValue.slice(0, 10)}
          onChange={(e) => onChange(e.target.value === "" ? null : e.target.value)}
          className={cn(inputCls, "[color-scheme:dark]")}
        />
      );

    case "tel":
      return (
        <input
          type="tel"
          value={stringValue}
          placeholder={field.placeholder}
          onChange={(e) => onChange(e.target.value === "" ? null : e.target.value)}
          className={inputCls}
        />
      );

    case "select":
      return (
        <SelectShell muted={stringValue === ""}>
          <select
            value={stringValue === "" ? SENTINEL_NONE : stringValue}
            onChange={(e) => onChange(e.target.value === SENTINEL_NONE ? null : e.target.value)}
            className={cn(inputCls, "appearance-none pr-10", stringValue === "" && "text-faint")}
          >
            <option value={SENTINEL_NONE}>— Selecionar —</option>
            {(field.options ?? []).map((o) => (
              <option key={o} value={o}>
                {o}
              </option>
            ))}
          </select>
        </SelectShell>
      );

    case "boolean": {
      const boolStr = value === true ? "true" : value === false ? "false" : SENTINEL_NONE;
      return (
        <SelectShell muted={boolStr === SENTINEL_NONE}>
          <select
            value={boolStr}
            onChange={(e) => {
              const v = e.target.value;
              onChange(v === SENTINEL_NONE ? null : v === "true");
            }}
            className={cn(inputCls, "appearance-none pr-10", boolStr === SENTINEL_NONE && "text-faint")}
          >
            <option value={SENTINEL_NONE}>— Selecionar —</option>
            <option value="false">Não</option>
            <option value="true">Sim</option>
          </select>
        </SelectShell>
      );
    }

    case "text":
    default:
      return (
        <input
          type="text"
          value={stringValue}
          placeholder={field.placeholder}
          onChange={(e) => onChange(e.target.value === "" ? null : e.target.value)}
          className={inputCls}
        />
      );
  }
}
